/**
 * SalesIntelTile — super-admin dashboard tile that surfaces the
 * personalised weekly Sales Intelligence Briefing on demand.
 *
 * Pairs with `services/sales_intelligence_digest.py` and the
 * `POST /api/admin/demo-director/sales-intel/send-now` endpoint:
 *   - "Preview" (dry-run)  → renders the SE's own briefing inline.
 *   - "Send to me now"     → fires the email via Resend.
 *
 * Why a tile? The cron job ships every Monday 08:00 UTC. SEs
 * preparing a Friday demo don't want to wait — they want their
 * "you haven't touched Save Preset in 14 days" nudge *now*. The
 * tile turns the API into a one-click utility.
 *
 * Renders nothing for non-super-admins (the underlying endpoint is
 * 403-gated, but suppressing the tile entirely avoids a confusing
 * empty card).
 */
import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Mail, Sparkles, Send, Eye } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';


const WINDOW_OPTIONS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
];


export default function SalesIntelTile({ currentUserEmail }) {
  const [windowDays, setWindowDays] = useState(7);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Pick out the row for the currently-logged-in SE so the inline
  // preview shows *their* briefing — not some other super-admin's.
  const myBriefing = useMemo(() => {
    if (!preview?.per_recipient || !currentUserEmail) return null;
    return preview.per_recipient.find(
      (r) => r.email?.toLowerCase() === currentUserEmail.toLowerCase(),
    ) || preview.per_recipient[0] || null;
  }, [preview, currentUserEmail]);

  const handlePreview = async () => {
    setPreviewing(true);
    setError(null);
    // Fire-and-forget telemetry — don't await, never block UI.
    api.post('/analytics/sales-intel/track', {
      event: 'preview',
      window_days: windowDays,
      page_path: window.location.pathname,
    }).catch(() => {});
    try {
      const res = await api.post(
        `/admin/demo-director/sales-intel/send-now?dry_run=true&window_days=${windowDays}`,
      );
      setPreview(res.data);
    } catch (e) {
      setError(e?.response?.status === 403 ? 'forbidden' : 'fetch_failed');
      toast.error("Couldn't load Sales Intel briefing preview");
    } finally {
      setPreviewing(false);
    }
  };

  const handleSendNow = async () => {
    api.post('/analytics/sales-intel/track', {
      event: 'send-now-confirm',
      window_days: windowDays,
      page_path: window.location.pathname,
    }).catch(() => {});
    if (!window.confirm(
      `Send the Sales Intelligence Briefing now to ${preview?.recipients?.length || '?'} super-admin(s)?\n\nThis dispatches real emails via Resend.`,
    )) {
      api.post('/analytics/sales-intel/track', {
        event: 'send-now-cancel',
        window_days: windowDays,
        page_path: window.location.pathname,
      }).catch(() => {});
      return;
    }
    setSending(true);
    try {
      const res = await api.post(
        `/admin/demo-director/sales-intel/send-now?dry_run=false&window_days=${windowDays}`,
      );
      api.post('/analytics/sales-intel/track', {
        event: 'send-now-dispatch',
        window_days: windowDays,
        page_path: window.location.pathname,
      }).catch(() => {});
      toast.success(
        `Sent ${res.data.sent} of ${res.data.recipients?.length || 0} briefing(s)`,
      );
    } catch (e) {
      toast.error(
        e?.response?.status === 403
          ? 'Super-admin only'
          : "Send failed — check Resend configuration",
      );
    } finally {
      setSending(false);
    }
  };

  if (error === 'forbidden') return null;

  return (
    <Card
      data-testid="sales-intel-tile"
      className="border-amber-200 bg-gradient-to-br from-white to-amber-50/40"
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-500" />
              Sales Intelligence Briefing
            </CardTitle>
            <CardDescription className="mt-0.5">
              Personalised toolkit-usage + preset-traction nudge. Auto-sends every Monday 08:00 UTC. Preview yours below or send now.
            </CardDescription>
          </div>
          <div
            className="inline-flex bg-white border border-slate-200 rounded-md p-0.5"
            data-testid="sales-intel-window-selector"
          >
            {WINDOW_OPTIONS.map((o) => (
              <button
                key={o.days}
                type="button"
                onClick={() => setWindowDays(o.days)}
                className={
                  'px-2 py-0.5 text-[11px] font-medium rounded transition-colors ' +
                  (windowDays === o.days
                    ? 'bg-amber-500 text-white'
                    : 'text-slate-600 hover:bg-slate-100')
                }
                data-testid={`sales-intel-window-${o.days}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-3">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handlePreview}
            disabled={previewing}
            data-testid="sales-intel-preview-btn"
            className="border-amber-300 text-amber-800 hover:bg-amber-100"
          >
            <Eye className="h-3.5 w-3.5 mr-1.5" />
            {previewing ? 'Loading…' : preview ? 'Refresh preview' : 'Preview my briefing'}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSendNow}
            disabled={sending || !preview}
            data-testid="sales-intel-send-btn"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Send className="h-3.5 w-3.5 mr-1.5" />
            {sending ? 'Sending…' : 'Send to me now'}
          </Button>
        </div>

        {!preview && !previewing && (
          <p
            className="text-xs text-slate-500"
            data-testid="sales-intel-empty-hint"
          >
            Click <em>Preview</em> to see your top features used, idle features, and your org's hottest preset for the last {windowDays} days.
          </p>
        )}

        {error === 'fetch_failed' && (
          <p className="text-xs text-rose-600" data-testid="sales-intel-error">
            Couldn't load briefing preview. Try again in a moment.
          </p>
        )}

        {myBriefing && (
          <div className="space-y-3" data-testid="sales-intel-preview-body">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Toolkit clicks</p>
                <p className="text-lg font-bold text-amber-700 tabular-nums">
                  {myBriefing.payload.total_actions}
                </p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Features used</p>
                <p className="text-lg font-bold text-emerald-700 tabular-nums">
                  {myBriefing.payload.distinct_actions_used}
                  <span className="text-xs text-slate-400">
                    /{myBriefing.payload.roster_size}
                  </span>
                </p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Recipients</p>
                <p className="text-lg font-bold text-slate-700 tabular-nums">
                  {preview?.recipients?.length || 0}
                </p>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-3">
              <p className="text-[11px] font-semibold text-slate-700 mb-1.5">
                <Mail className="inline h-3 w-3 mr-1 -mt-0.5 text-amber-500" />
                Subject preview
              </p>
              <p
                className="text-[12px] font-mono text-slate-800 break-words"
                data-testid="sales-intel-subject"
              >
                {myBriefing.subject}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-white border border-slate-200 rounded-md p-3">
                <p className="text-[11px] font-semibold text-emerald-700 mb-2">
                  Top 3 features you used
                </p>
                {myBriefing.payload.top_used.length === 0 ? (
                  <p className="text-[11px] text-slate-400">No clicks yet — try the toolkit.</p>
                ) : (
                  <ol
                    className="space-y-1"
                    data-testid="sales-intel-top-used"
                  >
                    {myBriefing.payload.top_used.map((u) => (
                      <li
                        key={u.action}
                        className="flex items-center justify-between text-[12px]"
                      >
                        <span className="text-slate-700">{u.label}</span>
                        <span className="font-bold text-emerald-700 tabular-nums">{u.count}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              <div className="bg-white border border-slate-200 rounded-md p-3">
                <p className="text-[11px] font-semibold text-rose-700 mb-2">
                  Try these next demo
                </p>
                {myBriefing.payload.top_unused.length === 0 ? (
                  <p className="text-[11px] text-emerald-600">Full literacy — every feature used.</p>
                ) : (
                  <ul
                    className="space-y-1"
                    data-testid="sales-intel-top-unused"
                  >
                    {myBriefing.payload.top_unused.map((u) => (
                      <li
                        key={u.action}
                        className="flex items-center gap-2 text-[12px]"
                      >
                        <span className="h-1 w-1 rounded-full bg-rose-400" />
                        <span className="text-slate-700">{u.label}</span>
                        <span className="text-[10px] font-mono text-slate-400">{u.action}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {myBriefing.payload.top_preset && (
              <div
                className="bg-sky-50 border border-sky-200 rounded-md p-3"
                data-testid="sales-intel-top-preset"
              >
                <p className="text-[10px] uppercase tracking-wider text-sky-700 font-semibold">
                  Top preset · your org
                </p>
                <p className="text-[14px] font-bold text-slate-800 mt-0.5">
                  {myBriefing.payload.top_preset.name}
                </p>
                <p className="text-[11px] text-slate-600">
                  Run {myBriefing.payload.top_preset.count} time(s) by your team
                </p>
              </div>
            )}

            {myBriefing.payload.top_whitepaper && (
              <div
                className="bg-amber-50 border border-amber-200 rounded-md p-3"
                data-testid="sales-intel-top-whitepaper"
              >
                <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">
                  Top whitepaper · your org
                </p>
                <p className="text-[14px] font-bold text-slate-800 mt-0.5">
                  {myBriefing.payload.top_whitepaper.label}
                </p>
                <p className="text-[11px] text-slate-600">
                  {myBriefing.payload.top_whitepaper.count} download{myBriefing.payload.top_whitepaper.count === 1 ? '' : 's'} by your prospects
                </p>
                <p className="text-[10px] text-amber-800 italic mt-1">
                  Lead with this asset in your follow-up — it&apos;s landing.
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
